import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import useSnack  from 'hooks/useSnack';
import Button from 'components/common/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Avatar from '@material-ui/core/Avatar';
import PersonIcon from '@material-ui/icons/Person';
import { makeStyles } from '@material-ui/core/styles';
import { blue } from '@material-ui/core/colors';
import AvatarEditor from 'react-avatar-edit';
import Spin from 'components/common/Spin';
import Storage from '@aws-amplify/storage';
import { UPDATE_USER } from 'graphql/Home';
import { updateUser } from 'reducers/user';

const useStyles = makeStyles(theme =>({
  profilePic: {
    width: 120,
    height: 120,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    cursor: 'pointer',
    position: 'relative',
    border: '2px solid',
    borderColor: theme.palette.background.default,
    marginRight: theme.spacing(4)
  },
  defaultAvatar: {
    fontSize: 100,
  },
  fabProgress: {
    color: blue[500],
    position: 'absolute',
    top: -5,
    left: -5,
    zIndex: 1,
  },
  picBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  fabAdd: {
    marginTop: 8,
  },
  editor: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fabCaption: {
    paddingLeft: theme.spacing(1)
  }
}));

const FileUpload = ({ sub, user }) => {
  const classes = useStyles();
  const snack = useSnack();
  const dispatch = useDispatch();
  const [showUploader, toggleUploader] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [update] = useMutation(UPDATE_USER);

  const onChangePic = () => {
    toggleUploader(true);
  };

  const onCrop = (base64) => {
    setFile(base64);
  };

  const onUpload = async () => {
    try {
      setSaving(true);
      const base64Data = new Buffer.from(file.replace(/^data:image\/\w+;base64,/, ""), 'base64');
      const resp = await Storage.put(`avatar-${sub}.png`, base64Data, {
        contentType: 'image/png',
      });
      const path = `https://weadmit-user-pics-bucket.s3.us-east-2.amazonaws.com/public/${resp.key}`;
      await update({ variables: { sub: sub, data: { user_pic: path } } });
      dispatch(updateUser({
        user_pic: path
      }));
      setSaving(false);
      toggleUploader(false);
      setFile();
      snack.success('Profile picture updated.');
    } catch (e) {
      snack.error(e.message);
      setSaving(false);
    }
  };

  if (showUploader) {
    return (
      <Spin spinning={saving} tip="Uploading...">
        <div className={classes.editor}>
          <AvatarEditor
            height={200}
            width={300}
            cropRadius={120}
            onCrop={onCrop}
            label="Choose profile picture"
          />
          <Box marginTop={2}>
            <Button color="secondary" onClick={() => toggleUploader(false)}>
              Cancel
            </Button>
            <Button
              disabled={!file}
              color="primary"
              variant="outlined"
              onClick={onUpload}
            >
              Save
            </Button>
          </Box>
        </div>
      </Spin>
    );
  }
  return (
    <Box className={classes.picBox}>
      <Avatar
        alt="avatar"
        src={user.user_pic}
        className={classes.profilePic}
      >
        {!user.user_pic && <PersonIcon className={classes.defaultAvatar} />}
      </Avatar>
      <Box>
        <Button
          className={classes.fabAdd}
          color="primary"
          onClick={onChangePic}
        >
          Change profile picture
        </Button>
        <Typography className={classes.fabCaption} variant="caption" component="p">at least 200x200, PNG or JPG file.</Typography>
      </Box>
    </Box>
  );
};

export default FileUpload;
